import { useMemo } from 'react'
import { WorkspaceWithLinkDescriptor } from '@synapex/shared2/entities'
import {
    UpdateVkOrdCreativeInputDto,
    VkOrdCreativeInputDto,
    VkOrdCreativeResponse,
} from '@synapex/shared2/gateway-rest-api'
import { CamelIdObj } from '@synapex/tools/typescript'
import {
    useMutation,
    UseMutationOptions,
    useQuery,
    useQueryClient,
    UseQueryOptions,
} from '@tanstack/react-query'
import { LinkDescriptorDto } from '@synapex/shared2/gateway-rest-api'

import { api } from '../base'

import { OmitWwld, pickDto } from '../utils'

import { apiQueryKey } from '../query-key'

export function useCreateVkOrdCreative(
    wwld: WorkspaceWithLinkDescriptor,
    {
        onSettled: onSettledOpt,
        ...opts
    }: Omit<
        UseMutationOptions<
            VkOrdCreativeResponse,
            Error,
            OmitWwld<VkOrdCreativeInputDto>
        >,
        'mutationFn'
    > = {}
) {
    const queryClient = useQueryClient()

    return useMutation<
        VkOrdCreativeResponse,
        Error,
        OmitWwld<VkOrdCreativeInputDto>
    >({
        ...opts,
        mutationFn: (body: OmitWwld<VkOrdCreativeInputDto>) => {
            return api.put<VkOrdCreativeResponse>(
                '/ord/creatives/',
                pickDto(VkOrdCreativeInputDto, { ...wwld, ...body })
            )
        },
        onSettled: (...args) => {
            const [_, __, variables] = args
            queryClient.invalidateQueries({
                queryKey: apiQueryKey.vkOrdCreatives(
                    wwld,
                    variables.creativeId
                ),
            })

            queryClient.invalidateQueries({
                queryKey: apiQueryKey.additionalOrdData(wwld),
            })

            queryClient.invalidateQueries({
                queryKey: apiQueryKey.markingFormData(wwld),
            })

            if (onSettledOpt) {
                onSettledOpt(...args)
            }
        },
    })
}

export function useUpdateVkOrdCreative(
    wwld: WorkspaceWithLinkDescriptor,
    {
        onSettled: onSettledOpt,
        ...opts
    }: Omit<
        UseMutationOptions<
            VkOrdCreativeResponse,
            Error,
            OmitWwld<UpdateVkOrdCreativeInputDto>
        >,
        'mutationFn'
    > = {}
) {
    const queryClient = useQueryClient()

    return useMutation<
        VkOrdCreativeResponse,
        Error,
        OmitWwld<UpdateVkOrdCreativeInputDto>
    >({
        ...opts,
        mutationFn: (body: OmitWwld<UpdateVkOrdCreativeInputDto>) => {
            return api.post<VkOrdCreativeResponse>(
                '/ord/creatives/',
                pickDto(UpdateVkOrdCreativeInputDto, { ...wwld, ...body })
            )
        },
        onSettled: (...args) => {
            const [_, __, variables] = args
            queryClient.invalidateQueries({
                queryKey: apiQueryKey.vkOrdCreatives(
                    wwld,
                    variables.creativeId
                ),
            })

            queryClient.invalidateQueries({
                queryKey: apiQueryKey.additionalOrdData(wwld),
            })

            // Инвалидируем кэш markingFormData при изменении креатива
            // Это необходимо, так как настройки редактора хранятся по creative_id
            queryClient.invalidateQueries({
                queryKey: apiQueryKey.markingFormData(wwld),
            })

            if (onSettledOpt) {
                onSettledOpt(...args)
            }
        },
    })
}

export type UseCreateVkOrdCreative = ReturnType<typeof useCreateVkOrdCreative>
export type UseUpdateVkOrdCreative = ReturnType<typeof useUpdateVkOrdCreative>

function useGetVkOrdCreatives(
    params: CamelIdObj<'creative', number | null | undefined> &
        WorkspaceWithLinkDescriptor,
    options?: Omit<
        UseQueryOptions<VkOrdCreativeResponse[], Error>,
        'queryKey' | 'queryFn'
    >
) {
    return useQuery<VkOrdCreativeResponse[], Error>({
        ...options,
        enabled: Boolean(params.creativeId),
        queryKey: apiQueryKey.vkOrdCreatives(params, params.creativeId),
        queryFn: () =>
            api.get(`/ord/creatives/${params.creativeId}`, {
                params: pickDto(LinkDescriptorDto, params),
            }),
    })
}

export type UseSavedEridProps = {
    draftContractId?: number | null
    agencyContractId?: number | null
    creativeId?: number | null
    customContractId?: number | null
} & WorkspaceWithLinkDescriptor

export const useSavedErid = ({
    draftContractId,
    creativeId,
    agencyContractId,
    customContractId,
    ...props
}: UseSavedEridProps) => {
    const { data: vkOrdCreatives = [] } = useGetVkOrdCreatives(
        {
            ...props,
            creativeId,
        },
        {
            // refetchOnMount: true,
            // staleTime: 5 * 1000,
        }
    )

    return useMemo(() => {
        if (
            !(
                creativeId &&
                (draftContractId || agencyContractId || customContractId)
            )
        ) {
            return { savedErid: null, savedKktu: null }
        }

        const vkOrdCreativeWithSelectedDraftContract = vkOrdCreatives.find(
            (vkoc) => {
                if (
                    agencyContractId &&
                    vkoc.vk_ord_agency_contract_id === agencyContractId
                ) {
                    return true
                }

                if (
                    draftContractId &&
                    vkoc.vk_ord_contract?.draft_contract_id === draftContractId
                ) {
                    return true
                }

                if (
                    customContractId &&
                    vkoc.vk_ord_contract?.custom_contract_id ===
                        customContractId
                ) {
                    return true
                }

                return false
            }
        )

        if (vkOrdCreativeWithSelectedDraftContract) {
            const { erid, kktu } = vkOrdCreativeWithSelectedDraftContract
            return { savedErid: erid, savedKktu: kktu }
        }

        return { savedErid: null, savedKktu: null }
    }, [
        vkOrdCreatives,
        creativeId,
        draftContractId,
        agencyContractId,
        customContractId,
    ])
}
export type UseSavedErid = ReturnType<typeof useSavedErid>
